import React, { useState, useEffect } from 'react';
import { Box, Button, Text, VStack, HStack, Badge, Spinner, useToast, Card, CardHeader, CardBody, Heading, Divider, Radio, RadioGroup, Stack } from '@chakra-ui/react';
import { PushNotifications } from '@capacitor/push-notifications';
import { fetchApi } from '../../../lib/apiClient';
import { useTranslation } from 'react-i18next';
import { NotificationService } from '../../../services/notification/NotificationService';
import { logger } from '../../../lib/logger';

const FCMTestPanel: React.FC = () => {
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState<string>('');
  const [tokenStatus, setTokenStatus] = useState<'checking' | 'registered' | 'not_registered' | 'error' | 'auth_error' | 'permission_denied'>('checking');
  const [isNative, setIsNative] = useState(false);
  const [platformInfo, setPlatformInfo] = useState<{
    isNative: boolean;
    isWeb: boolean;
    isElectron: boolean;
    os: string;
  } | null>(null);
  const [testResult, setTestResult] = useState<{
    status: 'idle' | 'sending' | 'success' | 'error';
    message?: string;
    details?: any;
  }>({ status: 'idle' });
  const [targetDevice, setTargetDevice] = useState<'current' | 'all'>('current');
  const [lastReceivedNotification, setLastReceivedNotification] = useState<any>(null);
  const toast = useToast();
  const { t } = useTranslation();

  // 获取通知服务实例
  const notificationServiceInstance = NotificationService.getInstance();

  // 检查平台信息并初始化
  useEffect(() => {
    const checkPlatform = async () => {
      // 使用统一的平台工具获取平台信息
      const info = notificationServiceInstance.getPlatformInfo();
      setPlatformInfo(info);
      setIsNative(info.isNative);

      console.log('[FCMTestPanel] Platform info:', info);

      // 检测是否为模拟器环境
      // 更准确的模拟器检测逻辑：
      // 1. 检查 User Agent 中的 Simulator 标识
      // 2. 检查 x86_64 架构（模拟器通常运行在 x86_64 上）
      // 3. 排除 localhost 检测，因为真机也可能通过开发服务器访问
      const isSimulator = info.isNative && (
        navigator.userAgent.includes('Simulator') ||
        navigator.userAgent.includes('x86_64')
      );

      if (isSimulator) {
        console.log('[FCMTestPanel] 检测到模拟器环境');
        toast({
          title: t('v0/notifications:fcm.simulatorDetected', "检测到模拟器环境"),
          description: t('v0/notifications:fcm.simulatorLimitation', "iOS 模拟器无法接收真实的推送通知，请使用真实设备测试"),
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
      }

      // 获取或创建设备ID
      try {
        // 从localStorage获取设备ID
        let storedDeviceId = localStorage.getItem('fcm_device_id');
        if (!storedDeviceId) {
          // 如果没有设备ID，创建一个新的
          storedDeviceId = `device_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
          localStorage.setItem('fcm_device_id', storedDeviceId);
          console.log('[FCMTestPanel] 创建新的设备ID:', storedDeviceId);
        } else {
          console.log('[FCMTestPanel] 使用已存储的设备ID:', storedDeviceId);
        }
        setDeviceId(storedDeviceId);
      } catch (error) {
        console.error('[FCMTestPanel] 获取设备ID失败:', error);
        // 如果localStorage失败，使用临时ID
        const tempDeviceId = `temp_${Date.now()}`;
        setDeviceId(tempDeviceId);
      }

      if (!info.isNative) {
        setTokenStatus('not_registered');
        toast({
          title: t('v0/notifications:fcm.nonNativePlatform', "非原生平台"),
          description: t('v0/notifications:fcm.onlyAvailableOnNative', "FCM 推送仅在原生 iOS/Android 平台上可用"),
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else {
        // 延迟检查令牌，确保deviceId已设置
        setTimeout(() => {
          checkDeviceToken();
        }, 100);
      }
    };

    checkPlatform();
  }, []);

  // 检查设备令牌
  const checkDeviceToken = async () => {
    try {
      setTokenStatus('checking');
      console.log('[FCMTestPanel] 开始检查设备令牌，设备ID:', deviceId);

      // 1. 检查权限
      const permissionStatus = await PushNotifications.checkPermissions();
      console.log('[FCMTestPanel] 权限检查结果:', permissionStatus);

      if (permissionStatus.receive !== 'granted') {
        console.log('[FCMTestPanel] Push notification permission not granted:', permissionStatus.receive);
        setTokenStatus('permission_denied');
        toast({
          title: t('v0/notifications:fcm.permissionRequired', "需要通知权限"),
          description: t('v0/notifications:fcm.permissionRequiredDesc', "请先授予推送通知权限，然后点击\"注册设备令牌\""),
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
        return;
      }

      // 2. 从后端获取当前设备的令牌
      const queryParams = deviceId ? `?deviceId=${deviceId}` : '';
      console.log('[FCMTestPanel] 查询设备令牌，URL:', `/notifications/device-token${queryParams}`);

      const response = await fetchApi(`/notifications/device-token${queryParams}`, {
        method: 'GET'
      });

      console.log('[FCMTestPanel] 设备令牌查询响应:', response);

      if (response.success && response.token) {
        setDeviceToken(response.token);
        setTokenStatus('registered');
        console.log('[FCMTestPanel] 找到已注册的设备令牌');
      } else {
        setTokenStatus('not_registered');
        console.log('[FCMTestPanel] 未找到设备令牌，需要注册');
      }
    } catch (error) {
      console.error('[FCMTestPanel] Failed to check device token:', error);

      // 检查是否是认证错误
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.log('[FCMTestPanel] 错误详情:', errorMessage);

      if (errorMessage.includes('401') || errorMessage.includes('未登录') || errorMessage.includes('Unauthorized')) {
        setTokenStatus('auth_error');
        toast({
          title: t('v0/notifications:fcm.authError', "认证失败"),
          description: t('v0/notifications:fcm.authErrorDesc', "用户认证已过期，请刷新页面重新登录"),
          status: "error",
          duration: 10000,
          isClosable: true,
        });
      } else if (errorMessage.includes('网络') || errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        setTokenStatus('error');
        toast({
          title: t('v0/notifications:fcm.networkError', "网络错误"),
          description: t('v0/notifications:fcm.networkErrorDesc', "网络连接失败，请检查网络连接后重试"),
          status: "error",
          duration: 8000,
          isClosable: true,
        });
      } else {
        setTokenStatus('error');
        toast({
          title: t('v0/notifications:fcm.unknownError', "未知错误"),
          description: `${t('v0/notifications:fcm.errorDetails', "错误详情")}: ${errorMessage}`,
          status: "error",
          duration: 8000,
          isClosable: true,
        });
      }
    }
  };

  // 注册设备令牌
  const registerDeviceToken = async () => {
    if (!isNative) {
      toast({
        title: t('v0/notifications:fcm.nonNativePlatform', "非原生平台"),
        description: t('v0/notifications:fcm.onlyAvailableOnNative', "FCM 推送仅在原生 iOS/Android 平台上可用"),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setTokenStatus('checking');
      logger.info('FCM注册流程开始', {
        deviceId,
        platform: platformInfo?.os,
        isNative,
        userAgent: navigator.userAgent
      });

      // 1. 请求权限
      logger.info('FCM步骤1: 请求推送通知权限');
      const permissionStatus = await PushNotifications.requestPermissions();
      logger.info('FCM权限请求结果', { permissionStatus });

      if (permissionStatus.receive !== 'granted') {
        logger.warn('FCM权限被拒绝', {
          permissionReceive: permissionStatus.receive,
          fullPermissionStatus: permissionStatus
        });
        toast({
          title: t('v0/notifications:fcm.permissionDenied', "权限被拒绝"),
          description: t('v0/notifications:fcm.userDeniedPermission', "用户拒绝了推送通知权限"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setTokenStatus('not_registered');
        return;
      }

      // 2. 注册设备
      logger.info('FCM步骤2: 调用 PushNotifications.register()');
      await PushNotifications.register();
      logger.info('FCM PushNotifications.register() 调用完成');

      // 3. 设置监听器获取令牌
      logger.info('FCM步骤3: 设置 registration 监听器');

      // 创建一个 Promise 来等待令牌或错误，带超时处理
      const registrationPromise = new Promise<string>((resolve, reject) => {
        let tokenListener: any;
        let errorListener: any;
        let timeoutId: NodeJS.Timeout;

        // 设置超时（15秒）
        timeoutId = setTimeout(async () => {
          logger.warn('FCM注册超时', { deviceId });

          // 清理监听器
          if (tokenListener) (await tokenListener).remove();
          if (errorListener) (await errorListener).remove();

          reject(new Error('FCM 注册超时，可能是模拟器环境或网络问题'));
        }, 15000);

        // 设置令牌接收监听器
        PushNotifications.addListener('registration', async (token) => {
          logger.info('FCM收到设备令牌', {
            tokenLength: token.value.length,
            tokenPrefix: token.value.substring(0, 20) + '...',
            deviceId
          });
          setDeviceToken(token.value);

          // 清理超时和监听器
          clearTimeout(timeoutId);
          if (tokenListener) (await tokenListener).remove();
          if (errorListener) (await errorListener).remove();

          resolve(token.value);
        }).then(listener => {
          tokenListener = listener;
        });

        // 设置错误监听器
        PushNotifications.addListener('registrationError', async (error) => {
          logger.error('FCM推送注册失败', error, { deviceId });

          // 清理超时和监听器
          clearTimeout(timeoutId);
          if (tokenListener) (await tokenListener).remove();
          if (errorListener) (await errorListener).remove();

          reject(error);
        }).then(listener => {
          errorListener = listener;
        });
      });

      // 等待令牌或错误
      try {
        const token = await registrationPromise;

        // 4. 将令牌发送到服务器
        logger.info('FCM步骤4: 发送令牌到服务器');
        const devicePlatform = platformInfo?.os === 'ios' ? 'ios' : 'android';

        logger.info('FCM准备发送令牌到服务器', {
          platform: devicePlatform,
          deviceId,
          tokenLength: token.length,
          platformInfo
        });

        const response = await fetchApi('/notifications/register-device', {
          method: 'POST',
          body: {
            token: token,
            platform: devicePlatform,
            deviceId: deviceId
          }
        });

        logger.info('FCM服务器响应', {
          success: response.success,
          message: response.message,
          hasError: !!response.error
        });

        setTokenStatus('registered');
        toast({
          title: t('v0/notifications:fcm.registrationSuccess', "注册成功"),
          description: t('v0/notifications:fcm.deviceTokenRegistered', "设备令牌已成功注册"),
          status: "success",
          duration: 5000,
          isClosable: true,
        });

      } catch (registrationError) {
        logger.error('FCM注册过程失败', registrationError, { deviceId });
        setTokenStatus('error');
        toast({
          title: t('v0/notifications:fcm.registrationFailed', "注册失败"),
          description: `FCM 注册错误: ${registrationError && typeof registrationError === 'object' ? JSON.stringify(registrationError) : String(registrationError)}`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }

      // 设置通知接收监听器（这个可以保持持久）
      logger.info('FCM步骤5: 设置 pushNotificationReceived 监听器');
      PushNotifications.addListener('pushNotificationReceived',
        (notification) => {
          logger.info('FCM收到推送通知', {
            title: notification.title,
            body: notification.body,
            hasData: !!notification.data,
            deviceId
          });
          setLastReceivedNotification(notification);
          toast({
            title: t('v0/notifications:fcm.notificationReceived', "收到推送通知"),
            description: notification.title,
            status: "info",
            duration: 5000,
            isClosable: true,
          });
        }
      );

      logger.info('FCM注册流程完成', { deviceId });

    } catch (error) {
      logger.error('FCM注册设备令牌过程中发生异常', error, { deviceId });
      setTokenStatus('error');
      toast({
        title: t('v0/notifications:fcm.registrationFailed', "注册失败"),
        description: `${t('v0/notifications:fcm.errorOccurred', "发生错误")}: ${error instanceof Error ? error.message : String(error)}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // 删除当前设备令牌
  const deleteDeviceToken = async () => {
    if (!deviceToken) {
      toast({
        title: t('v0/notifications:fcm.noToken', "没有令牌"),
        description: t('v0/notifications:fcm.noTokenToDelete', "没有可删除的设备令牌"),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await fetchApi('/notifications/device-token', {
        method: 'DELETE',
        body: {
          token: deviceToken
        }
      });

      if (response.success) {
        setDeviceToken(null);
        setTokenStatus('not_registered');
        toast({
          title: t('v0/notifications:fcm.deleteSuccess', "删除成功"),
          description: t('v0/notifications:fcm.tokenDeleted', "设备令牌已删除"),
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('v0/notifications:fcm.deleteFailed', "删除失败"),
          description: response.error || t('v0/notifications:fcm.unknownError', "未知错误"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      logger.error('FCM删除设备令牌失败', error, { deviceId });
      toast({
        title: t('v0/notifications:fcm.deleteFailed', "删除失败"),
        description: `${t('v0/notifications:fcm.errorOccurred', "发生错误")}: ${error instanceof Error ? error.message : String(error)}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // 发送测试推送通知
  const sendTestPushNotification = async () => {
    if (tokenStatus !== 'registered') {
      logger.warn('FCM尝试发送测试通知但设备未注册', { tokenStatus, deviceId });
      toast({
        title: t('v0/notifications:fcm.notRegistered', "未注册"),
        description: t('v0/notifications:fcm.registerFirst', "请先注册设备令牌"),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setTestResult({ status: 'sending' });

      const testPayload = {
        title: t('v0/notifications:fcm.testNotificationTitle', "FCM 测试通知"),
        body: `${t('v0/notifications:fcm.testNotificationBody', "这是一条测试通知")} - ${new Date().toLocaleTimeString()}`,
        data: {
          type: 'fcm_test',
          timestamp: new Date().toISOString()
        },
        deviceId: deviceId,
        platform: platformInfo?.os === 'ios' ? 'ios' : 'android',
        targetDevice: targetDevice
      };

      logger.info('FCM发送测试通知', {
        deviceId,
        platform: testPayload.platform,
        targetDevice,
        hasDeviceToken: !!deviceToken
      });

      const response = await fetchApi('/notifications/test', {
        method: 'POST',
        body: testPayload
      });

      logger.info('FCM测试通知响应', {
        success: response.success,
        message: response.message,
        deviceId
      });

      setTestResult({
        status: response.success ? 'success' : 'error',
        message: response.message,
        details: response
      });

      toast({
        title: response.success ? t('v0/notifications:fcm.sendSuccess', "发送成功") : t('v0/notifications:fcm.sendFailed', "发送失败"),
        description: response.message,
        status: response.success ? "success" : "error",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      logger.error('FCM发送测试通知失败', error, { deviceId });
      setTestResult({
        status: 'error',
        message: error instanceof Error ? error.message : String(error)
      });

      toast({
        title: t('v0/notifications:fcm.sendFailed', "发送失败"),
        description: `${t('v0/notifications:fcm.errorOccurred', "发生错误")}: ${error instanceof Error ? error.message : String(error)}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Card mb={4}>
      <CardHeader pb={0}>
        <Heading size="md">{t('v0/notifications:sections.fcmTest', "FCM 推送测试")}</Heading>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {/* 平台信息 */}
          <Box>
            <Text fontWeight="semibold">{t('v0/notifications:fcm.platformStatus', "平台状态")}:</Text>
            <HStack mt={1} flexWrap="wrap">
              {platformInfo?.os && (
                <Badge colorScheme={platformInfo.os === 'ios' ? "blue" : "green"}>
                  {platformInfo.os.toUpperCase()}
                </Badge>
              )}
              <Badge colorScheme={isNative ? "green" : "red"}>
                {isNative ? t('v0/notifications:platform.native', "原生平台") : t('v0/notifications:platform.nonNative', "非原生平台")}
              </Badge>

              {/* 模拟器检测 */}
              {isNative && (navigator.userAgent.includes('Simulator') || navigator.userAgent.includes('x86_64')) && (
                <Badge colorScheme="orange">
                  {t('v0/notifications:platform.simulator', "模拟器")}
                </Badge>
              )}

              <Text fontSize="sm" color="gray.500">
                {isNative ?
                  (navigator.userAgent.includes('Simulator') || navigator.userAgent.includes('x86_64')) ?
                    t('v0/notifications:fcm.simulatorLimited', "模拟器环境，FCM 推送受限") :
                    t('v0/notifications:fcm.fcmAvailable', "FCM 推送可用") :
                  t('v0/notifications:fcm.fcmNotAvailable', "FCM 推送不可用")
                }
              </Text>
            </HStack>

            {/* 设备ID */}
            {deviceId && (
              <Box mt={2}>
                <Text fontSize="sm" fontWeight="semibold">{t('v0/notifications:fcm.deviceId', "设备ID")}:</Text>
                <Box p={2} bg="gray.100" borderRadius="md" fontSize="xs" fontFamily="monospace" overflowX="auto">
                  <Text isTruncated>{deviceId}</Text>
                </Box>
              </Box>
            )}

            {platformInfo && (
              <Box mt={2} p={2} bg="gray.100" borderRadius="md" fontSize="xs" fontFamily="monospace" overflowX="auto">
                <Text>{JSON.stringify(platformInfo, null, 2)}</Text>
              </Box>
            )}
          </Box>

          <Divider />

          {/* 设备令牌状态 */}
          <Box>
            <Text fontWeight="semibold">{t('v0/notifications:fcm.deviceTokenStatus', "设备令牌状态")}:</Text>
            <HStack mt={1}>
              {tokenStatus === 'checking' && <Spinner size="sm" />}
              <Badge
                colorScheme={
                  tokenStatus === 'registered' ? "green" :
                  tokenStatus === 'checking' ? "blue" :
                  tokenStatus === 'auth_error' ? "red" :
                  tokenStatus === 'permission_denied' ? "orange" :
                  tokenStatus === 'error' ? "red" : "yellow"
                }
              >
                {tokenStatus === 'registered' ? t('v0/notifications:fcm.registered', "已注册") :
                 tokenStatus === 'checking' ? t('v0/notifications:fcm.checking', "检查中") :
                 tokenStatus === 'auth_error' ? t('v0/notifications:fcm.authError', "认证失败") :
                 tokenStatus === 'permission_denied' ? t('v0/notifications:fcm.permissionDenied', "权限被拒绝") :
                 tokenStatus === 'error' ? t('v0/notifications:fcm.error', "错误") : t('v0/notifications:fcm.notRegistered', "未注册")}
              </Badge>
            </HStack>

            {/* 状态说明和操作建议 */}
            {tokenStatus === 'auth_error' && (
              <Box mt={2} p={3} bg="red.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="red.400">
                <Text fontSize="sm" color="red.700">
                  <strong>{t('v0/notifications:fcm.authErrorTitle', "认证失败")}</strong>
                </Text>
                <Text fontSize="sm" color="red.600" mt={1}>
                  {t('v0/notifications:fcm.authErrorSolution', "用户认证已过期，请刷新页面重新登录后再试")}
                </Text>
              </Box>
            )}

            {tokenStatus === 'permission_denied' && (
              <Box mt={2} p={3} bg="orange.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="orange.400">
                <Text fontSize="sm" color="orange.700">
                  <strong>{t('v0/notifications:fcm.permissionDeniedTitle', "权限被拒绝")}</strong>
                </Text>
                <Text fontSize="sm" color="orange.600" mt={1}>
                  {t('v0/notifications:fcm.permissionDeniedSolution', "请在设备设置中允许此应用发送通知，然后点击\"注册设备令牌\"")}
                </Text>
              </Box>
            )}

            {deviceToken && (
              <Box mt={2} p={2} bg="gray.100" borderRadius="md" fontSize="xs" fontFamily="monospace" overflowX="auto">
                <Text isTruncated>{deviceToken}</Text>
              </Box>
            )}

            <HStack mt={2} spacing={2}>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={registerDeviceToken}
                isDisabled={!isNative || tokenStatus === 'checking'}
              >
                {t('v0/notifications:fcm.registerDeviceToken', "注册设备令牌")}
              </Button>
              <Button
                size="sm"
                onClick={checkDeviceToken}
                isDisabled={!isNative || tokenStatus === 'checking'}
              >
                {t('v0/notifications:fcm.refreshStatus', "刷新状态")}
              </Button>
              <Button
                size="sm"
                colorScheme="red"
                onClick={deleteDeviceToken}
                isDisabled={!isNative || tokenStatus !== 'registered' || !deviceToken}
              >
                {t('v0/notifications:fcm.deleteToken', "删除令牌")}
              </Button>
            </HStack>
          </Box>

          <Divider />

          {/* 测试推送 */}
          <Box>
            <Text fontWeight="semibold">{t('v0/notifications:fcm.testPushNotification', "测试推送通知")}:</Text>

            {/* 目标设备选择 */}
            <Box mt={2}>
              <Text fontSize="sm" fontWeight="semibold">{t('v0/notifications:fcm.targetDevice', "目标设备")}:</Text>
              <RadioGroup onChange={(value) => setTargetDevice(value as 'current' | 'all')} value={targetDevice} mt={1}>
                <Stack direction="row">
                  <Radio value="current">{t('v0/notifications:fcm.currentDevice', "当前设备")}</Radio>
                  <Radio value="all">{t('v0/notifications:fcm.allDevices', "所有设备")}</Radio>
                </Stack>
              </RadioGroup>
            </Box>

            <HStack mt={3}>
              <Button
                colorScheme="green"
                onClick={sendTestPushNotification}
                isLoading={testResult.status === 'sending'}
                isDisabled={!isNative || tokenStatus !== 'registered'}
              >
                {t('v0/notifications:fcm.sendTestPush', "发送测试推送")}
              </Button>
            </HStack>

            {testResult.status !== 'idle' && (
              <Box mt={2} p={2} bg={testResult.status === 'success' ? "green.50" : "red.50"} borderRadius="md">
                <Text fontWeight="semibold" color={testResult.status === 'success' ? "green.500" : "red.500"}>
                  {testResult.status === 'sending' ? t('v0/notifications:fcm.sending', "发送中...") :
                   testResult.status === 'success' ? t('v0/notifications:fcm.sendSuccess', "发送成功") : t('v0/notifications:fcm.sendFailed', "发送失败")}
                </Text>
                {testResult.message && (
                  <Text fontSize="sm" mt={1}>{testResult.message}</Text>
                )}

                {/* 模拟器环境特殊说明 */}
                {testResult.status === 'success' && isNative && (navigator.userAgent.includes('Simulator') || navigator.userAgent.includes('x86_64')) && (
                  <Box mt={2} p={2} bg="orange.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="orange.400">
                    <Text fontSize="sm" color="orange.700">
                      <strong>{t('v0/notifications:fcm.simulatorNote', "模拟器说明")}</strong>
                    </Text>
                    <Text fontSize="sm" color="orange.600" mt={1}>
                      {t('v0/notifications:fcm.simulatorNoteDesc', "通知已发送到后端，但 iOS 模拟器无法接收真实的推送通知。要测试实际接收效果，请使用真实的 iOS 设备。")}
                    </Text>
                  </Box>
                )}

                {testResult.details && (
                  <Box mt={2} p={2} bg="gray.100" borderRadius="md" fontSize="xs" fontFamily="monospace" overflowX="auto" maxH="200px" overflowY="auto">
                    <pre>{JSON.stringify(testResult.details, null, 2)}</pre>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* 最近收到的通知 */}
          {lastReceivedNotification && (
            <>
              <Divider />
              <Box>
                <Text fontWeight="semibold">{t('v0/notifications:fcm.lastReceivedNotification', "最近收到的通知")}:</Text>
                <Box mt={2} p={2} bg="blue.50" borderRadius="md">
                  <Text fontWeight="semibold">{lastReceivedNotification.title}</Text>
                  <Text fontSize="sm" mt={1}>{lastReceivedNotification.body}</Text>
                  <Box mt={2} p={2} bg="gray.100" borderRadius="md" fontSize="xs" fontFamily="monospace" overflowX="auto" maxH="200px" overflowY="auto">
                    <pre>{JSON.stringify(lastReceivedNotification.data, null, 2)}</pre>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default FCMTestPanel;
